import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { TransactionType } from '@algoan/dashboard/model';
import { monitoring } from '@algoan/dashboard/utils';

export type TransactionTypeTranslator = (type: TransactionType | undefined) => string;
const loggedUnknownTransactionType: Array<TransactionType | undefined> = [];
const logUnknownTransactionTypeToDatadogRum = (type: TransactionType | undefined): void => {
  if (loggedUnknownTransactionType.includes(type)) {
    return;
  }

  monitoring.logError({
    fingerprint: 'unknown_transaction_type',
    message: 'Unknown type for transaction',
    context: { type: type, level: 'error' },
  });

  loggedUnknownTransactionType.push(type);
};

export const useTransactionTypeTranslator = (): TransactionTypeTranslator => {
  const intl = useIntl();
  return useCallback<TransactionTypeTranslator>(
    (type) => {
      if (!type || !(type in transactionTypeMessages)) {
        logUnknownTransactionTypeToDatadogRum(type);
        return intl.formatMessage(transactionTypeMessages.notDetermined);
      }

      return intl.formatMessage(transactionTypeMessages[type]);
    },
    [intl],
  );
};

const transactionTypeMessages = defineMessages({
  notDetermined: {
    id: 'transaction.type.not_determined',
    defaultMessage: 'Not determined',
    description: 'Label for unknown transaction type',
  },
  [TransactionType.CARD]: {
    id: 'transaction.type.CARD',
    defaultMessage: 'Card',
    description: 'Label of transaction type CARD',
  },
  [TransactionType.CARD_REFUND]: {
    id: 'transaction.type.CARD_REFUND',
    defaultMessage: 'Card refund',
    description: 'Label of transaction type CARD_REFUND',
  },
  [TransactionType.CASH_DEPOSIT]: {
    id: 'transaction.type.CASH_DEPOSIT',
    defaultMessage: 'Cash deposit',
    description: 'Label of transaction type CASH_DEPOSIT',
  },
  [TransactionType.CASH_WITHDRAWAL]: {
    id: 'transaction.type.CASH_WITHDRAWAL',
    defaultMessage: 'Cash withdrawal',
    description: 'Label of transaction type CASH_WITHDRAWAL',
  },
  [TransactionType.CHECK]: {
    id: 'transaction.type.CHECK',
    defaultMessage: 'Check',
    description: 'Label of transaction type CHECK',
  },
  [TransactionType.CHECK_DEPOSIT]: {
    id: 'transaction.type.CHECK_DEPOSIT',
    defaultMessage: 'Check deposit',
    description: 'Label of transaction type CHECK_DEPOSIT',
  },
  [TransactionType.CREDIT]: {
    id: 'transaction.type.CREDIT',
    defaultMessage: 'Credit',
    description: 'Label of transaction type CREDIT',
  },
  [TransactionType.DEBIT]: {
    id: 'transaction.type.DEBIT',
    defaultMessage: 'Debit',
    description: 'Label of transaction type DEBIT',
  },
  [TransactionType.DIRECT_DEBIT]: {
    id: 'transaction.type.DIRECT_DEBIT',
    defaultMessage: 'Direct debit',
    description: 'Label of transaction type DIRECT_DEBIT',
  },
  [TransactionType.INCOMING_CARD]: {
    id: 'transaction.type.INCOMING_CARD',
    defaultMessage: 'Credit on card',
    description: 'Label of transaction type INCOMING_CARD',
  },
  [TransactionType.INCOMING_INTERNAL_TRANSFER]: {
    id: 'transaction.type.INCOMING_INTERNAL_TRANSFER',
    defaultMessage: 'Incoming internal transfer',
    description: 'Label of transaction type INCOMING_INTERNAL_TRANSFER',
  },
  [TransactionType.INCOMING_TRANSFER]: {
    id: 'transaction.type.INCOMING_TRANSFER',
    defaultMessage: 'Incoming transfer',
    description: 'Label of transaction type INCOMING_TRANSFER',
  },
  [TransactionType.OUTGOING_INTERNAL_TRANSFER]: {
    id: 'transaction.type.OUTGOING_INTERNAL_TRANSFER',
    defaultMessage: 'Outgoing internal transfer',
    description: 'Label of transaction type OUTGOING_INTERNAL_TRANSFER',
  },
  [TransactionType.OUTGOING_TRANSFER]: {
    id: 'transaction.type.OUTGOING_TRANSFER',
    defaultMessage: 'Outgoing transfer',
    description: 'Label of transaction type OUTGOING_TRANSFER',
  },
});

export type TransactionTypeWithCountTranslator = (type: TransactionType | undefined, count: number) => string;

export const useTransactionTypeWithCountTranslator = (): TransactionTypeWithCountTranslator => {
  const intl = useIntl();
  return useCallback<TransactionTypeWithCountTranslator>(
    (type, count) => {
      if (!type || !(type in transactionTypeMessages)) {
        logUnknownTransactionTypeToDatadogRum(type);
        return intl.formatMessage(transactionTypeMessagesWithCount.notDetermined);
      }

      return intl.formatMessage(transactionTypeMessagesWithCount[type], { count });
    },
    [intl],
  );
};

const transactionTypeMessagesWithCount = defineMessages({
  notDetermined: {
    id: 'transaction.type_plural.not_determined',
    defaultMessage: 'Not determined',
    description: 'Label for unknown transaction type',
  },
  [TransactionType.CARD]: {
    id: 'transaction.type_plural.CARD',
    defaultMessage: '{count, plural, one {# payment by card} other {# payments by card}}',
    description: 'Label of transaction type CARD',
  },
  [TransactionType.CASH_WITHDRAWAL]: {
    id: 'transaction.type_plural.CASH_WITHDRAWAL',
    defaultMessage: '{count, plural, one {# cash withdrawal} other {# cash withdrawals}}',
    description: 'Label of transaction type CASH_WITHDRAWAL',
  },
  [TransactionType.CHECK]: {
    id: 'transaction.type_plural.CHECK',
    defaultMessage: '{count, plural, one {# check} other {# checks}}',
    description: 'Label of transaction type CHECK',
  },
  [TransactionType.DEBIT]: {
    id: 'transaction.type_plural.DEBIT',
    defaultMessage: '{count, plural, one {# debit} other {# debits}}',
    description: 'Label of transaction type DEBIT',
  },
  [TransactionType.DIRECT_DEBIT]: {
    id: 'transaction.type_plural.DIRECT_DEBIT',
    defaultMessage: '{count, plural, one {# direct debit} other {# direct debits}}',
    description: 'Label of transaction type DIRECT_DEBIT',
  },
  [TransactionType.INCOMING_CARD]: {
    id: 'transaction.type_plural.INCOMING_CARD',
    defaultMessage: '{count, plural, one {# credit on card} other {# credits on card}}',
    description: 'Label of transaction type INCOMING_CARD',
  },
  [TransactionType.OUTGOING_INTERNAL_TRANSFER]: {
    id: 'transaction.type_plural.OUTGOING_INTERNAL_TRANSFER',
    defaultMessage: '{count, plural, one {# outgoing internal transfer} other {# outgoing internal transfers}}',
    description: 'Label of transaction type OUTGOING_INTERNAL_TRANSFER',
  },
  [TransactionType.OUTGOING_TRANSFER]: {
    id: 'transaction.type_plural.OUTGOING_TRANSFER',
    defaultMessage: '{count, plural, one {# outgoing transfer} other {# outgoing transfers}}',
    description: 'Label of transaction type OUTGOING_TRANSFER',
  },
  // TODO: THE BELOW HAVE NO PLURAL FORM TRANSLATION YET
  [TransactionType.INCOMING_INTERNAL_TRANSFER]: {
    id: 'transaction.type.INCOMING_INTERNAL_TRANSFER',
    defaultMessage: 'Incoming internal transfer',
    description: 'Label of transaction type INCOMING_INTERNAL_TRANSFER',
  },
  [TransactionType.INCOMING_TRANSFER]: {
    id: 'transaction.type.INCOMING_TRANSFER',
    defaultMessage: 'Incoming transfer',
    description: 'Label of transaction type INCOMING_TRANSFER',
  },
  [TransactionType.CHECK_DEPOSIT]: {
    id: 'transaction.type.CHECK_DEPOSIT',
    defaultMessage: 'Check deposit',
    description: 'Label of transaction type CHECK_DEPOSIT',
  },
  [TransactionType.CREDIT]: {
    id: 'transaction.type.CREDIT',
    defaultMessage: 'Credit',
    description: 'Label of transaction type CREDIT',
  },
  [TransactionType.CARD_REFUND]: {
    id: 'transaction.type.CARD_REFUND',
    defaultMessage: 'Card refund',
    description: 'Label of transaction type CARD_REFUND',
  },
  [TransactionType.CASH_DEPOSIT]: {
    id: 'transaction.type.CASH_DEPOSIT',
    defaultMessage: 'Cash deposit',
    description: 'Label of transaction type CASH_DEPOSIT',
  },
});
